/**
 * Utility functions for FAQ page state management
 * Helper functions for managing drawer, modal, and editing states
 */

import { FAQ } from "../../../types";
import { FaqManagementState } from "../types/faq-types";

/**
 * Reset FAQ management state to initial values
 * @returns Initial state object
 */
export const resetFaqState = (): FaqManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: false,
  editingFaq: null,
  deletingFaq: null,
});

/**
 * Open edit drawer with selected FAQ
 * @param setState - State setter function
 * @param faq - FAQ to edit
 */
export const openEditDrawer = (
  setState: React.Dispatch<React.SetStateAction<FaqManagementState>>,
  faq: FAQ
): void => {
  setState((prev) => ({
    ...prev,
    editingFaq: faq,
    isDrawerOpen: true,
  }));
};

/**
 * Open create drawer for new FAQ
 * @param setState - State setter function
 */
export const openCreateDrawer = (
  setState: React.Dispatch<React.SetStateAction<FaqManagementState>>
): void => {
  setState((prev) => ({
    ...prev,
    editingFaq: null,
    isDrawerOpen: true,
  }));
};

/**
 * Close drawer and reset editing state
 * @param setState - State setter function
 */
export const closeDrawer = (
  setState: React.Dispatch<React.SetStateAction<FaqManagementState>>
): void => {
  setState((prev) => ({
    ...prev,
    isDrawerOpen: false,
    editingFaq: null,
  }));
};

/**
 * Open delete modal with selected FAQ
 * @param setState - State setter function
 * @param faq - FAQ to delete
 */
export const openDeleteModal = (
  setState: React.Dispatch<React.SetStateAction<FaqManagementState>>,
  faq: FAQ
): void => {
  setState((prev) => ({
    ...prev,
    deletingFaq: faq,
    isDeleteModalOpen: true,
  }));
};

/**
 * Close delete modal and reset deleting state
 * @param setState - State setter function
 */
export const closeDeleteModal = (
  setState: React.Dispatch<React.SetStateAction<FaqManagementState>>
): void => {
  setState((prev) => ({
    ...prev,
    isDeleteModalOpen: false,
    deletingFaq: null,
  }));
};

/**
 * Get drawer title based on editing state
 * @param editingFaq - Currently editing FAQ
 * @returns Drawer title string
 */
export const getDrawerTitle = (editingFaq: FAQ | null): string => {
  return editingFaq ? "Редактировать FAQ" : "Создать FAQ";
};

/**
 * Get display question for FAQ
 * @param faq - FAQ object
 * @returns Display question string
 */
export const getFaqQuestion = (faq: FAQ): string => {
  return faq.question || "Без вопроса";
};

/**
 * Get truncated answer for FAQ display
 * @param faq - FAQ object
 * @param maxLength - Maximum length for truncation
 * @returns Truncated answer string
 */
export const getFaqAnswerPreview = (
  faq: FAQ,
  maxLength: number = 100
): string => {
  const answer = faq.answer || "";
  if (answer.length <= maxLength) {
    return answer;
  }
  return `${answer.substring(0, maxLength)}...`;
};

/**
 * Validate FAQ form data
 * @param data - FAQ form data
 * @returns Validation result with errors
 */
export const validateFaqData = (data: {
  question: string;
  answer: string;
}): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  if (!data.question || data.question.trim().length < 5) {
    errors.push("Вопрос должен содержать минимум 5 символов");
  }

  if (!data.answer || data.answer.trim().length < 10) {
    errors.push("Ответ должен содержать минимум 10 символов");
  }

  if (data.question && data.question.length > 500) {
    errors.push("Вопрос не должен превышать 500 символов");
  }

  if (data.answer && data.answer.length > 2000) {
    errors.push("Ответ не должен превышать 2000 символов");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
